import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import type { GraphData } from "../lib/types";

interface Props {
  onOpenNote: (id: string) => void;
  rootId?: string;
}

interface Node {
  id: string;
  title: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  deg: number;
  rank: number; // 0 = höchster Degree — steuert Label-LOD beim Zoomen
  size: number; // Basis-Radius in Screen-Pixeln (degree-skaliert)
  cat: number; // Kategorie-Index (Top-Level-Folder)
  tags: string[];
  phase: number;
}
interface Edge {
  source: string;
  target: string;
  offset: number; // Phase für Synapsen-Partikel
  rest: number; // Federlänge, degree-skaliert — Hubs bekommen größere Fans
  cat: number; // Kategorie wenn beide Endpunkte gleich, sonst -1
}

interface Category {
  id: string | null;
  name: string;
  count: number;
  core: string; // Hex für Kerne/Partikel/Legende
  soft: string; // "rgba(r,g,b," — Alpha wird angehängt
}

interface SimState {
  nodes: Node[];
  edges: Edge[];
  byId: Map<string, Node>;
  adj: Map<string, Set<string>>;
  cats: Category[];
  /**
   * Globaler Multiplikator auf Federlängen + Repulsion. Wird per Feedback-Loop
   * so geregelt, dass der Graph die Brain-Fläche füllt — statische Konstanten
   * können das nicht, weil die nötige Spreizung von der Topologie abhängt
   * (ein Hub mit 60 Leaves kollabiert sonst immer auf Federlänge).
   */
  spread: number;
}

/** Neon-Palette für Kategorien — Reihenfolge nach Cluster-Größe. */
const PALETTE: { core: string; soft: string }[] = [
  { core: "#22d3ee", soft: "rgba(34,211,238," }, // cyan
  { core: "#a78bfa", soft: "rgba(167,139,250," }, // violet
  { core: "#f472b6", soft: "rgba(244,114,182," }, // pink
  { core: "#34d399", soft: "rgba(52,211,153," }, // emerald
  { core: "#fbbf24", soft: "rgba(251,191,36," }, // amber
  { core: "#38bdf8", soft: "rgba(56,189,248," }, // sky
  { core: "#a3e635", soft: "rgba(163,230,53," }, // lime
  { core: "#fb7185", soft: "rgba(251,113,133," }, // rose
];
const UNCAT: { core: string; soft: string } = { core: "#94a3b8", soft: "rgba(148,163,184," };

function buildCategories(data: GraphData): Category[] {
  const counts = new Map<string | null, number>();
  for (const n of data.nodes) {
    // ?? null: solange ein altes Backend läuft, fehlt folder_id im Response
    counts.set(n.folder_id ?? null, (counts.get(n.folder_id ?? null) ?? 0) + 1);
  }
  const cats: Category[] = (data.folders ?? [])
    .filter((f) => counts.has(f.id))
    .map((f) => ({ id: f.id as string | null, name: f.name, count: counts.get(f.id)! }))
    .sort((a, b) => b.count - a.count)
    .map((c, i) => ({ ...c, ...PALETTE[i % PALETTE.length] }));
  if (counts.has(null)) {
    cats.push({ id: null, name: "Unsortiert", count: counts.get(null)!, ...UNCAT });
  }
  return cats;
}

/**
 * Brain-shaped Graph mit semantischem Zoom und Kategorie-Clustern.
 *
 * Kategorien = Top-Level-Folder der Notes. Jede Kategorie bekommt einen
 * Anker im Gehirn ("Lobe") — Nodes werden dorthin gezogen, Links zwischen
 * Kategorien sind weiche Federn, damit die Cluster sich räumlich trennen.
 *
 * Zoom ist semantisch: Node-Radien, Linienstärken und Fonts sind konstant in
 * Screen-Pixeln. Reinzoomen vergrößert die Abstände, nicht die Punkte.
 * Labels erscheinen gestaffelt nach Degree-Rang. Hover dimmt alles außer dem
 * Node und seinen direkten Verknüpfungen.
 */
export function GraphView({ onOpenNote, rootId }: Props) {
  const { data } = useQuery({
    queryKey: ["graph", rootId ?? "all"],
    queryFn: () => api.getGraph(rootId, 3),
  });

  const cats = useMemo(() => (data ? buildCategories(data) : []), [data]);

  // Tag-Statistik für die Filter-Chips: Top-Tags nach Häufigkeit
  const tagStats = useMemo(() => {
    if (!data) return [];
    const counts = new Map<string, number>();
    for (const n of data.nodes) {
      for (const tg of n.tags ?? []) counts.set(tg, (counts.get(tg) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 14);
  }, [data]);

  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const tagFilterRef = useRef<Set<string>>(selectedTags);
  tagFilterRef.current = selectedTags;

  function toggleTag(name: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const stateRef = useRef<SimState | null>(null);
  const hoverRef = useRef<string | null>(null);
  const dprRef = useRef(window.devicePixelRatio || 1);

  const viewRef = useRef({ scale: 1, ox: 0, oy: 0 });
  const dragRef = useRef<{ active: boolean; lastX: number; lastY: number; moved: boolean }>({
    active: false,
    lastX: 0,
    lastY: 0,
    moved: false,
  });

  // Init / Reset on data change
  useEffect(() => {
    if (!data) return;
    const c = canvasRef.current;
    const w = c?.clientWidth ?? 800;
    const h = c?.clientHeight ?? 600;

    const deg = new Map<string, number>();
    const adj = new Map<string, Set<string>>();
    for (const e of data.edges) {
      deg.set(e.source, (deg.get(e.source) ?? 0) + 1);
      deg.set(e.target, (deg.get(e.target) ?? 0) + 1);
      if (!adj.has(e.source)) adj.set(e.source, new Set());
      if (!adj.has(e.target)) adj.set(e.target, new Set());
      adj.get(e.source)!.add(e.target);
      adj.get(e.target)!.add(e.source);
    }

    const catIndex = new Map<string | null, number>(cats.map((c2, i) => [c2.id, i]));
    const fallbackCat = Math.max(0, cats.length - 1);
    const catOf = (folderId: string | null) => catIndex.get(folderId) ?? fallbackCat;

    const nodes = initLayout(data, w, h, deg, catOf, cats.length);
    // Degree-Rang für Label-LOD: rank 0 = wichtigster Hub
    [...nodes].sort((a, b) => b.deg - a.deg).forEach((n, i) => (n.rank = i));

    const nodeCat = new Map(nodes.map((n) => [n.id, n.cat]));
    const edges: Edge[] = data.edges.map((e, i) => {
      const ca = nodeCat.get(e.source) ?? fallbackCat;
      const cb = nodeCat.get(e.target) ?? fallbackCat;
      return {
        source: e.source,
        target: e.target,
        offset: (i * 0.137) % 1,
        // Linear mit Degree statt sqrt: ein Hub mit 60 Edges braucht einen Ring
        // mit Umfang für 60 Nodes — die Federlänge muss entsprechend mitwachsen.
        rest: REST_BASE + 2.2 * ((deg.get(e.source) ?? 0) + (deg.get(e.target) ?? 0)),
        cat: ca === cb ? ca : -1,
      };
    });

    stateRef.current = {
      nodes,
      edges,
      byId: new Map(nodes.map((n) => [n.id, n])),
      adj,
      cats,
      spread: 2,
    };
  }, [data, cats]);

  // Tick + Resize + Wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      dprRef.current = dpr;
      const w = canvas!.clientWidth;
      const h = canvas!.clientHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // React's onWheel ist passive — preventDefault funktioniert nur via raw listener.
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const v = viewRef.current;
      const rect = canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0015);
      const newScale = clamp(v.scale * factor, 0.2, 8);
      // Zoom um Cursor: Weltpunkt unter Cursor bleibt fix
      const k = newScale / v.scale;
      v.ox = mx - (mx - v.ox) * k;
      v.oy = my - (my - v.oy) * k;
      v.scale = newScale;
    }
    canvas.addEventListener("wheel", onWheel, { passive: false });

    function tick() {
      const s = stateRef.current;
      const c = canvasRef.current;
      if (s && c) {
        step(s, c.clientWidth, c.clientHeight);
        draw(ctx, c, dprRef.current, viewRef.current, s, hoverRef.current, tagFilterRef.current);
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener("wheel", onWheel);
    };
  }, []);

  function screenToWorld(sx: number, sy: number) {
    const v = viewRef.current;
    return { x: (sx - v.ox) / v.scale, y: (sy - v.oy) / v.scale };
  }

  function eventToNode(e: React.MouseEvent<HTMLCanvasElement>): Node | null {
    const c = canvasRef.current;
    const s = stateRef.current;
    if (!c || !s) return null;
    const rect = c.getBoundingClientRect();
    const wpt = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const scale = viewRef.current.scale;
    let best: Node | null = null;
    let bestD = Infinity;
    for (const n of s.nodes) {
      // Trefferzone konstant in Screen-px, größere Hubs = größere Zone
      const hitR = (n.size + 8) / scale;
      const d = (n.x - wpt.x) ** 2 + (n.y - wpt.y) ** 2;
      if (d < hitR * hitR && d < bestD) {
        best = n;
        bestD = d;
      }
    }
    return best;
  }

  function zoomBy(factor: number) {
    const c = canvasRef.current;
    if (!c) return;
    const v = viewRef.current;
    const mx = c.clientWidth / 2;
    const my = c.clientHeight / 2;
    const newScale = clamp(v.scale * factor, 0.2, 8);
    const k = newScale / v.scale;
    v.ox = mx - (mx - v.ox) * k;
    v.oy = my - (my - v.oy) * k;
    v.scale = newScale;
  }

  function resetView() {
    viewRef.current = { scale: 1, ox: 0, oy: 0 };
  }

  return (
    <div className="relative h-full w-full bg-[#04050a]">
      {!data && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500">
          Lade Graph…
        </div>
      )}
      {data && data.nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500">
          Keine Notes — leg ein paar an, dann erscheint hier der Graph.
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        style={{ cursor: "grab" }}
        onMouseDown={(e) => {
          dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY, moved: false };
          if (canvasRef.current) canvasRef.current.style.cursor = "grabbing";
        }}
        onMouseUp={(e) => {
          const drag = dragRef.current;
          dragRef.current.active = false;
          if (canvasRef.current) canvasRef.current.style.cursor = "grab";
          // Click nur wenn nicht gedraggt — sonst öffnet jeder Pan eine Note
          if (!drag.moved) {
            const n = eventToNode(e);
            if (n) onOpenNote(n.id);
          }
        }}
        onMouseLeave={() => {
          dragRef.current.active = false;
          hoverRef.current = null;
          if (canvasRef.current) canvasRef.current.style.cursor = "grab";
        }}
        onMouseMove={(e) => {
          const drag = dragRef.current;
          if (drag.active) {
            const dx = e.clientX - drag.lastX;
            const dy = e.clientY - drag.lastY;
            if (Math.abs(dx) + Math.abs(dy) > 2) drag.moved = true;
            viewRef.current.ox += dx;
            viewRef.current.oy += dy;
            drag.lastX = e.clientX;
            drag.lastY = e.clientY;
            return;
          }
          const n = eventToNode(e);
          hoverRef.current = n?.id ?? null;
          if (canvasRef.current) canvasRef.current.style.cursor = n ? "pointer" : "grab";
        }}
      />
      {/* Tag-Filter */}
      {data && tagStats.length > 0 && (
        <div className="absolute left-3 top-3 max-w-[45%] rounded border border-cyan-500/10 bg-zinc-950/70 px-2 py-1.5 backdrop-blur">
          <div className="flex flex-wrap items-center gap-1">
            {tagStats.map((tg) => {
              const active = selectedTags.has(tg.name);
              return (
                <button
                  key={tg.name}
                  onClick={() => toggleTag(tg.name)}
                  className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                    active
                      ? "border-amber-300/80 bg-amber-300/15 text-amber-200 shadow-[0_0_8px_rgba(252,211,77,0.35)]"
                      : "border-cyan-500/20 text-cyan-300/70 hover:border-cyan-400/50 hover:text-cyan-200"
                  }`}
                >
                  #{tg.name} <span className="opacity-50">{tg.count}</span>
                </button>
              );
            })}
            {selectedTags.size > 0 && (
              <button
                onClick={() => setSelectedTags(new Set())}
                className="rounded-full border border-zinc-600 px-2 py-0.5 text-[10px] text-zinc-400 hover:border-zinc-400 hover:text-zinc-200"
                title="Filter zurücksetzen"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}
      {/* Kategorie-Legende */}
      {data && cats.length > 0 && (
        <div className="pointer-events-none absolute bottom-2 left-3 flex flex-col gap-1 rounded border border-cyan-500/10 bg-zinc-950/70 px-2.5 py-2 text-[11px] backdrop-blur">
          {cats.map((c) => (
            <div key={c.id ?? "__none"} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: c.core, boxShadow: `0 0 6px ${c.core}` }}
              />
              <span className="text-zinc-300">{c.name}</span>
              <span className="text-zinc-500">{c.count}</span>
            </div>
          ))}
        </div>
      )}
      {/* Zoom-Controls */}
      <div className="absolute right-3 top-3 flex flex-col gap-1 rounded border border-cyan-500/20 bg-zinc-950/70 p-1 text-xs text-cyan-200 backdrop-blur">
        <button
          className="w-7 rounded px-2 py-0.5 hover:bg-cyan-500/10"
          onClick={() => zoomBy(1.3)}
          title="Zoom in"
        >
          +
        </button>
        <button
          className="w-7 rounded px-2 py-0.5 hover:bg-cyan-500/10"
          onClick={() => zoomBy(1 / 1.3)}
          title="Zoom out"
        >
          −
        </button>
        <button
          className="w-7 rounded px-2 py-0.5 text-[10px] hover:bg-cyan-500/10"
          onClick={resetView}
          title="Reset View"
        >
          ⟳
        </button>
      </div>
      {data && (
        <div className="pointer-events-none absolute bottom-2 right-3 rounded border border-cyan-500/10 bg-zinc-950/70 px-2 py-1 text-[10px] text-cyan-300/70 backdrop-blur">
          {data.nodes.length} nodes · {data.edges.length} edges · scroll = zoom · drag = pan ·
          hover = Verknüpfungen
        </div>
      )}
    </div>
  );
}

// ─── Brain-Silhouette ────────────────────────────────────────────────────────

/**
 * Kontrollpunkte (normalized [-1..1]) — Gehirn im Seitenprofil, Blick nach links.
 * Obere Kante wellig (Gyri-Bumps), rechts unten Cerebellum mit Kerbe.
 */
const BRAIN_OUTLINE: [number, number][] = [
  [-0.92, 0.18], // Stirn unten
  [-1.0, -0.18], // Stirn
  [-0.9, -0.5], // Stirn oben
  [-0.7, -0.64],
  [-0.5, -0.84], // Gyrus-Bump
  [-0.3, -0.72],
  [-0.08, -0.92], // Scheitel
  [0.14, -0.78],
  [0.36, -0.88], // Gyrus-Bump
  [0.58, -0.68],
  [0.8, -0.5], // Okzipital
  [0.97, -0.18], // Hinterkopf
  [1.0, 0.14],
  [0.86, 0.4], // Einbuchtung über Cerebellum
  [0.97, 0.6], // Cerebellum hinten
  [0.8, 0.86], // Cerebellum unten
  [0.52, 0.84],
  [0.4, 0.58], // Kerbe Cerebellum/Temporallappen
  [0.18, 0.72],
  [-0.16, 0.66], // Unterseite
  [-0.5, 0.56], // Temporallappen
  [-0.78, 0.4],
];

/** Dekorative Furchen (Gyri) — quadratische Kurven [start, control, end] */
const GYRI: [number, number][][] = [
  [
    [-0.72, -0.28],
    [-0.42, -0.62],
    [-0.08, -0.4],
  ],
  [
    [-0.55, 0.08],
    [-0.15, -0.25],
    [0.3, -0.28],
  ],
  [
    [-0.25, 0.4],
    [0.08, 0.12],
    [0.45, 0.1],
  ],
  [
    [0.4, -0.55],
    [0.64, -0.32],
    [0.58, -0.02],
  ],
  [
    [-0.85, -0.1],
    [-0.65, -0.35],
    [-0.4, -0.18],
  ],
  // Cerebellum-Streifen
  [
    [0.5, 0.72],
    [0.68, 0.55],
    [0.92, 0.62],
  ],
  [
    [0.55, 0.82],
    [0.73, 0.66],
    [0.93, 0.72],
  ],
];

interface BrainShape {
  poly: [number, number][];
  cx: number;
  cy: number;
  bw: number;
  bh: number;
}

let shapeCache: { key: string; shape: BrainShape } | null = null;

function brainShape(w: number, h: number): BrainShape {
  const key = `${w}x${h}`;
  if (shapeCache?.key === key) return shapeCache.shape;
  const bw = Math.min(w * 0.86, (h * 0.92) / 0.76);
  const bh = bw * 0.76;
  const cx = w / 2;
  const cy = h / 2;
  const ctrl = BRAIN_OUTLINE.map(
    ([x, y]) => [cx + (x * bw) / 2, cy + (y * bh) / 2] as [number, number],
  );
  const poly = sampleClosedCatmullRom(ctrl, 5);
  shapeCache = { key, shape: { poly, cx, cy, bw, bh } };
  return shapeCache.shape;
}

/** Anker-Position einer Kategorie — verteilt auf einem inneren Ring. */
function catAnchor(k: number, total: number, shape: BrainShape): [number, number] {
  if (total <= 1) return [shape.cx, shape.cy];
  // Start links (Frontallappen), dann im Uhrzeigersinn ums Gehirn
  const a = Math.PI + (k * 2 * Math.PI) / total;
  return [shape.cx + Math.cos(a) * shape.bw * 0.27, shape.cy + Math.sin(a) * shape.bh * 0.27];
}

/** Glättet die Kontrollpunkte zu einem geschlossenen Polygon (Catmull-Rom). */
function sampleClosedCatmullRom(pts: [number, number][], perSeg: number): [number, number][] {
  const out: [number, number][] = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    for (let j = 0; j < perSeg; j++) {
      const t = j / perSeg;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push([
        0.5 *
          (2 * p1[0] +
            (-p0[0] + p2[0]) * t +
            (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
            (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 *
          (2 * p1[1] +
            (-p0[1] + p2[1]) * t +
            (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
            (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
      ]);
    }
  }
  return out;
}

function pointInPoly(x: number, y: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// ─── Layout ──────────────────────────────────────────────────────────────────

function initLayout(
  data: GraphData,
  w: number,
  h: number,
  deg: Map<string, number>,
  catOf: (folderId: string | null) => number,
  catTotal: number,
): Node[] {
  const shape = brainShape(w, h);
  return data.nodes.map((n) => {
    const cat = catOf(n.folder_id);
    // Start nahe am Kategorie-Anker (mit Hash-Jitter) — konvergiert schneller
    // in die Cluster-Struktur als globales Scattering.
    const [ax, ay] = catAnchor(cat, catTotal, shape);
    let seed = hash(n.id);
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const jx = ((seed % 100000) / 100000) * 2 - 1;
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const jy = ((seed % 100000) / 100000) * 2 - 1;
    const d = deg.get(n.id) ?? 0;
    return {
      id: n.id,
      title: n.title,
      x: ax + jx * shape.bw * 0.08,
      y: ay + jy * shape.bh * 0.08,
      vx: 0,
      vy: 0,
      deg: d,
      rank: 0,
      size: 3 + Math.min(6, 1.5 * Math.sqrt(d)),
      cat,
      tags: n.tags ?? [],
      phase: (hash(n.id) % 628) / 100,
    };
  });
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

// ─── Physik ──────────────────────────────────────────────────────────────────

const REPEL = 2600;
const SPRING = 0.035;
const REST_BASE = 60;
const DAMP = 0.8;
// Zug zum Kategorie-Anker — muss gegen Springs zu fremden Clustern ankommen
const CLUSTER_PULL = 0.012;
// Cross-Kategorie-Springs stark abgeschwächt, damit Cluster sich trennen können
const CROSS_SPRING = 0.3;
const MAX_VEL = 8;
const SUBSTEPS = 3;
// Ziel-Ausdehnung der Node-Wolke, gemessen als RMS-Radius in Silhouetten-
// normalisierten Koordinaten. Volle Ellipsen-Füllung wäre ~0.71 — bei 0.6
// bleibt etwas Luft zur Kontur, bevor die Boundary-Force dagegenhält.
const TARGET_RMS = 0.6;

function step(s: SimState, w: number, h: number) {
  const shape = brainShape(w, h);

  // Auto-Spread-Regler: Wolken-Ausdehnung messen, Federlängen nachziehen.
  if (s.nodes.length > 1) {
    let mx = 0;
    let my = 0;
    for (const n of s.nodes) {
      mx += n.x;
      my += n.y;
    }
    mx /= s.nodes.length;
    my /= s.nodes.length;
    let acc = 0;
    for (const n of s.nodes) {
      const nx = (n.x - mx) / (shape.bw / 2);
      const ny = (n.y - my) / (shape.bh / 2);
      acc += nx * nx + ny * ny;
    }
    const rms = Math.sqrt(acc / s.nodes.length);
    s.spread = clamp(s.spread * (1 + 0.03 * (TARGET_RMS - rms)), 1, 10);
  }

  for (let i = 0; i < SUBSTEPS; i++) physicsTick(s, shape);
}

function physicsTick(s: SimState, shape: BrainShape) {
  const { nodes, edges, byId, cats } = s;
  const anchors = cats.map((_, k) => catAnchor(k, cats.length, shape));

  // Repulsion (n² — bei <500 Nodes problemlos)
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i];
      const b = nodes[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      let d2 = dx * dx + dy * dy;
      if (d2 < 9) d2 = 9;
      const f = (REPEL * s.spread) / d2;
      const d = Math.sqrt(d2);
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx -= fx;
      a.vy -= fy;
      b.vx += fx;
      b.vy += fy;
    }
  }

  // Springs auf Edges, Federlänge degree-skaliert
  for (const e of edges) {
    const a = byId.get(e.source);
    const b = byId.get(e.target);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const d = Math.max(1, Math.sqrt(dx * dx + dy * dy));
    // Federlänge × Auto-Spread, gedeckelt damit eine einzelne Edge nicht
    // breiter als das halbe Gehirn werden will
    const rest = Math.min(e.rest * s.spread, shape.bw * 0.42);
    let f = (d - rest) * SPRING;
    // Links über Kategorie-Grenzen dürfen lang sein — sonst zerren sie die
    // Cluster wieder zusammen
    if (e.cat === -1) f *= CROSS_SPRING;
    const fx = (dx / d) * f;
    const fy = (dy / d) * f;
    a.vx += fx;
    a.vy += fy;
    b.vx -= fx;
    b.vy -= fy;
  }

  // Cluster-Pull zum Kategorie-Anker + Confinement in der Silhouette
  for (const n of nodes) {
    const [ax, ay] = anchors[n.cat] ?? [shape.cx, shape.cy];
    n.vx += (ax - n.x) * CLUSTER_PULL;
    n.vy += (ay - n.y) * CLUSTER_PULL;
    if (!pointInPoly(n.x, n.y, shape.poly)) {
      let bx = shape.cx;
      let by = shape.cy;
      let bd = Infinity;
      for (const [px, py] of shape.poly) {
        const d = (px - n.x) ** 2 + (py - n.y) ** 2;
        if (d < bd) {
          bd = d;
          bx = px;
          by = py;
        }
      }
      const d = Math.sqrt(bd) || 1;
      const k = Math.min(d * 0.05, 2.5);
      n.vx += ((bx - n.x) / d) * k;
      n.vy += ((by - n.y) / d) * k;
    }
  }

  // Integrate mit Damping + Velocity-Cap
  for (const n of nodes) {
    n.vx *= DAMP;
    n.vy *= DAMP;
    const v2 = n.vx * n.vx + n.vy * n.vy;
    if (v2 > MAX_VEL * MAX_VEL) {
      const scale = MAX_VEL / Math.sqrt(v2);
      n.vx *= scale;
      n.vy *= scale;
    }
    n.x += n.vx;
    n.y += n.vy;
  }
}

// ─── Render ──────────────────────────────────────────────────────────────────

function draw(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  dpr: number,
  view: { scale: number; ox: number; oy: number },
  s: SimState,
  hoverId: string | null,
  tagFilter: Set<string>,
) {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const t = performance.now() * 0.001;
  const { nodes, edges, byId, adj, cats } = s;
  const scale = view.scale;

  // Tag-Filter: Set der Node-IDs, die mindestens einen gewählten Tag tragen (OR)
  let tagMatch: Set<string> | null = null;
  if (tagFilter.size > 0) {
    tagMatch = new Set();
    for (const n of nodes) {
      if (n.tags.some((tg) => tagFilter.has(tg))) tagMatch.add(n.id);
    }
  }

  // 1) Background in Screen-Space (unbeeinflusst vom Zoom)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.8);
  bg.addColorStop(0, "#0a1428");
  bg.addColorStop(0.55, "#050810");
  bg.addColorStop(1, "#020308");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = "rgba(34,211,238,0.035)";
  ctx.lineWidth = 1;
  const grid = 40;
  ctx.beginPath();
  for (let x = view.ox % grid; x < w; x += grid) {
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, h);
  }
  for (let y = view.oy % grid; y < h; y += grid) {
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(w, y + 0.5);
  }
  ctx.stroke();

  const vig = ctx.createRadialGradient(
    w / 2,
    h / 2,
    Math.min(w, h) * 0.3,
    w / 2,
    h / 2,
    Math.max(w, h) * 0.7,
  );
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, w, h);

  // 2) World-Space (Zoom/Pan)
  ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * view.ox, dpr * view.oy);

  const shape = brainShape(w, h);
  drawBrain(ctx, shape, t, scale);

  // 3) Lobe-Glow: weicher Schein pro Kategorie hinter ihrem Cluster
  if (cats.length > 1) {
    const stats = cats.map(() => ({ n: 0, x: 0, y: 0, r2: 0 }));
    for (const n of nodes) {
      const st = stats[n.cat];
      st.n++;
      st.x += n.x;
      st.y += n.y;
    }
    for (const st of stats) {
      if (st.n > 0) {
        st.x /= st.n;
        st.y /= st.n;
      }
    }
    for (const n of nodes) {
      const st = stats[n.cat];
      st.r2 += (n.x - st.x) ** 2 + (n.y - st.y) ** 2;
    }
    for (let k = 0; k < cats.length; k++) {
      const st = stats[k];
      if (st.n < 2) continue;
      const r = Math.max(70, Math.sqrt(st.r2 / st.n) * 1.9);
      const g = ctx.createRadialGradient(st.x, st.y, 0, st.x, st.y, r);
      g.addColorStop(0, cats[k].soft + "0.10)");
      g.addColorStop(1, cats[k].soft + "0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(st.x, st.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const nbs = hoverId !== null ? (adj.get(hoverId) ?? null) : null;

  // 4) Edges — Bezier mit Synapsen-Partikeln. Farbe nach Kategorie, Links
  //    zwischen Kategorien neutral. Bei Hover: nur Verknüpfungen hell.
  for (const e of edges) {
    const a = byId.get(e.source);
    const b = byId.get(e.target);
    if (!a || !b) continue;
    const incident = hoverId !== null && (e.source === hoverId || e.target === hoverId);
    // Dimmen: bei Hover alles außer den eigenen Verknüpfungen; bei aktivem
    // Tag-Filter alles, wo nicht beide Endpunkte matchen.
    const dimmed =
      hoverId !== null
        ? !incident
        : tagMatch !== null && !(tagMatch.has(e.source) && tagMatch.has(e.target));
    const cat = e.cat >= 0 ? cats[e.cat] : null;

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const curve = Math.min(len * 0.16, 46);
    const mx = (a.x + b.x) / 2 + px * curve;
    const my = (a.y + b.y) / 2 + py * curve;

    ctx.shadowColor = incident
      ? "rgba(226,254,255,0.9)"
      : cat
        ? cat.soft + "0.45)"
        : "rgba(148,163,184,0.3)";
    ctx.shadowBlur = incident ? 14 : dimmed ? 0 : 6;
    ctx.strokeStyle = incident
      ? "rgba(236,254,255,0.95)"
      : dimmed
        ? cat
          ? cat.soft + "0.05)"
          : "rgba(148,163,184,0.04)"
        : cat
          ? cat.soft + "0.3)"
          : "rgba(148,163,184,0.14)";
    ctx.lineWidth = (incident ? 1.8 : 0.8) / scale;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.quadraticCurveTo(mx, my, b.x, b.y);
    ctx.stroke();

    if (dimmed) continue;
    const tp = (t * 0.28 + e.offset) % 1;
    const omt = 1 - tp;
    const qx = omt * omt * a.x + 2 * omt * tp * mx + tp * tp * b.x;
    const qy = omt * omt * a.y + 2 * omt * tp * my + tp * tp * b.y;
    ctx.shadowColor = cat?.core ?? "#a5f3fc";
    ctx.shadowBlur = incident ? 16 : 9;
    ctx.fillStyle = incident ? "#ecfeff" : (cat?.core ?? "#a5f3fc");
    ctx.beginPath();
    ctx.arc(qx, qy, (incident ? 2.4 : 1.6) / scale, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.shadowBlur = 0;

  // 5) Nodes — Größe konstant in Screen-px (deshalb /scale): Zoomen vergrößert
  //    die Abstände, nicht die Punkte. Farbe nach Kategorie.
  for (const n of nodes) {
    const isHover = n.id === hoverId;
    const isNb = nbs?.has(n.id) ?? false;
    const isTagged = tagMatch?.has(n.id) ?? false;
    const dimmed =
      hoverId !== null ? !isHover && !isNb : tagMatch !== null && !isTagged;
    const cat = cats[n.cat] ?? UNCAT;
    const pulse = 0.85 + 0.15 * Math.sin(t * 1.8 + n.phase);
    const r = (isHover ? n.size + 3 : n.size * pulse) / scale;

    ctx.globalAlpha = dimmed ? 0.15 : 1;
    ctx.shadowColor = isHover ? "#e0f7fa" : cat.soft + "0.95)";
    ctx.shadowBlur = isHover ? 24 : 14;
    ctx.fillStyle = isHover ? "#a5f3fc" : cat.soft + (isNb ? "0.95)" : "0.85)");
    ctx.beginPath();
    ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
    ctx.fill();

    // Amber-Ring markiert Tag-Treffer — hebt sich von allen Cluster-Farben ab
    if (isTagged && !dimmed) {
      ctx.shadowColor = "#fcd34d";
      ctx.shadowBlur = 10;
      ctx.strokeStyle = "rgba(252,211,77,0.9)";
      ctx.lineWidth = 1.4 / scale;
      ctx.beginPath();
      ctx.arc(n.x, n.y, r + 3 / scale, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(n.x, n.y, (isHover ? 2.6 : 1.4) / scale, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // 6) Labels — LOD: Hubs zuerst, mehr Labels je tiefer der Zoom.
  //    Bei Hover nur der Node + seine Nachbarn.
  const budget = Math.max(6, Math.round(8 * scale * scale));
  const vx0 = -view.ox / scale - 80;
  const vx1 = (w - view.ox) / scale + 80;
  const vy0 = -view.oy / scale - 40;
  const vy1 = (h - view.oy) / scale + 40;
  const labelPx = 11 / scale;
  ctx.font = `${labelPx}px Inter, system-ui, sans-serif`;
  ctx.textAlign = "center";
  for (const n of nodes) {
    const isHover = n.id === hoverId;
    const isNb = nbs?.has(n.id) ?? false;
    const isTagged = tagMatch?.has(n.id) ?? false;
    if (hoverId !== null && !isHover && !isNb) continue;
    if (tagMatch !== null && hoverId === null && !isTagged) continue;
    // Tag-Treffer bekommen immer ein Label — der Filter ist ja gerade dazu
    // da, diese Notes zu finden
    if (!isHover && !isNb && !isTagged && n.rank >= budget) continue;
    if (!isHover && (n.x < vx0 || n.x > vx1 || n.y < vy0 || n.y > vy1)) continue;

    const ty = n.y - (n.size + 9) / scale;
    if (isHover) {
      const padX = 6 / scale;
      const padY = 3 / scale;
      const tagPx = 9 / scale;
      const tagLine = n.tags.length > 0 ? n.tags.map((tg) => `#${tg}`).join(" ") : null;
      const tw = ctx.measureText(n.title).width;
      let plateW = tw;
      if (tagLine) {
        ctx.font = `${tagPx}px Inter, system-ui, sans-serif`;
        plateW = Math.max(tw, ctx.measureText(tagLine).width);
        ctx.font = `${labelPx}px Inter, system-ui, sans-serif`;
      }
      const plateH = labelPx + (tagLine ? tagPx + 3 / scale : 0) + padY;
      ctx.fillStyle = "rgba(8,12,24,0.85)";
      ctx.fillRect(n.x - plateW / 2 - padX, ty - labelPx + padY / 2, plateW + padX * 2, plateH);
      ctx.fillStyle = "#ecfeff";
      ctx.fillText(n.title, n.x, ty);
      if (tagLine) {
        ctx.font = `${tagPx}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = "rgba(252,211,77,0.85)";
        ctx.fillText(tagLine, n.x, ty + tagPx + 3 / scale);
        ctx.font = `${labelPx}px Inter, system-ui, sans-serif`;
      }
    } else {
      ctx.fillStyle = isTagged
        ? "rgba(253,230,138,0.92)"
        : isNb
          ? "rgba(236,254,255,0.9)"
          : "rgba(203,213,225,0.66)";
      ctx.fillText(n.title, n.x, ty);
    }
  }
}

function drawBrain(ctx: CanvasRenderingContext2D, shape: BrainShape, t: number, scale: number) {
  const { poly, cx, cy, bw, bh } = shape;
  const pulse = 0.5 + 0.5 * Math.sin(t * 0.8);

  // Membran-Fill — ganz subtil
  tracePoly(ctx, poly);
  ctx.fillStyle = "rgba(34,211,238,0.025)";
  ctx.fill();

  // Outer Glow, leicht pulsierend
  ctx.shadowColor = "rgba(34,211,238,0.5)";
  ctx.shadowBlur = 34;
  ctx.strokeStyle = `rgba(34,211,238,${0.1 + 0.07 * pulse})`;
  ctx.lineWidth = 8 / scale;
  tracePoly(ctx, poly);
  ctx.stroke();

  // Hauptkontur
  ctx.shadowBlur = 14;
  ctx.strokeStyle = "rgba(34,211,238,0.45)";
  ctx.lineWidth = 1.6 / scale;
  tracePoly(ctx, poly);
  ctx.stroke();

  // Gyri-Furchen
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(34,211,238,0.12)";
  ctx.lineWidth = 1 / scale;
  for (const [a, c, b] of GYRI) {
    ctx.beginPath();
    ctx.moveTo(cx + (a[0] * bw) / 2, cy + (a[1] * bh) / 2);
    ctx.quadraticCurveTo(
      cx + (c[0] * bw) / 2,
      cy + (c[1] * bh) / 2,
      cx + (b[0] * bw) / 2,
      cy + (b[1] * bh) / 2,
    );
    ctx.stroke();
  }
}

function tracePoly(ctx: CanvasRenderingContext2D, poly: [number, number][]) {
  ctx.beginPath();
  ctx.moveTo(poly[0][0], poly[0][1]);
  for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i][0], poly[i][1]);
  ctx.closePath();
}

function clamp(x: number, lo: number, hi: number) {
  return x < lo ? lo : x > hi ? hi : x;
}
